import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';
import { refreshEntitlements } from '../utils/entitlements';
import ConfirmDialog from '../components/ConfirmDialog';
import PasswordInput from '../components/PasswordInput';
import '../css/parvekshak.css';

// ── Users / Roles / Menu-permission management (RBAC) ──
// Backend: /ui/web-users, /ui/roles, /ui/menus (admin-only). A non-admin with the
// Users & Roles menu (the site admin) gets the users list only (PRVSK-110): add
// site admins / end users, enable/disable others, and delete users created by
// someone of their own role (the server marks those rows can_delete) — no Roles
// tab, role changes or passwords.

// What a site admin may create, and how built-in roles read without /ui/roles.
const NONADMIN_ROLES = [['enduser', 'End User'], ['siteadmin', 'Site Admin']];
const BUILTIN_LABELS = { admin: 'Administrator', siteadmin: 'Site Admin', enduser: 'End User' };
// The support account logs in under its own name but is shown as its display_name
// ("parvekshak-support"), so the customer can see whose account it is.
const shownName = u => u?.display_name || u?.username;

const box = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 14 };
const inp = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', fontSize: 13 };
const btn = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const btnPri = { ...btn, background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff' };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' };
const td = { padding: '8px 10px', fontSize: 13, borderTop: '1px solid var(--border)' };

// Compact camera picker — collapsed by default (selected cameras shown as
// removable chips), opening a search + checkbox dropdown on click. A flat
// tag list that's always expanded doesn't scale to fleets in the hundreds
// (finding one by scanning is impractical, and 300 pills inline is its own
// mess), and even a search box alone still forces the admin to recall the
// name first — this stays out of the way when closed and gives a scoped,
// scrollable place to browse/search when open. Groups by zone when zones are
// actually in use. Select/Clear act on whatever's currently filtered, so
// e.g. searching "gate" then "Select filtered" bulk-picks every gate camera.
function CameraPicker({ cameras, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { if (!open) setQ(''); }, [open]);

  const byIdx = useMemo(() => new Map(cameras.map(c => [c.idx, c])), [cameras]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cameras;
    return cameras.filter(c =>
      (c.name || '').toLowerCase().includes(s) || (c.zone || '').toLowerCase().includes(s));
  }, [cameras, q]);

  const hasZones = useMemo(() => cameras.some(c => (c.zone || '').trim()), [cameras]);

  const groups = useMemo(() => {
    if (!hasZones) return [['', filtered]];
    const g = {};
    filtered.forEach(c => {
      const z = (c.zone || '').trim() || 'Unassigned';
      (g[z] = g[z] || []).push(c);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered, hasZones]);

  const toggle = (idx) =>
    onChange(selected.includes(idx) ? selected.filter(x => x !== idx) : [...selected, idx]);
  const remove = (idx) => onChange(selected.filter(x => x !== idx));
  const selectFiltered = () =>
    onChange([...new Set([...selected, ...filtered.map(c => c.idx)])]);
  const clearFiltered = () => {
    const drop = new Set(filtered.map(c => c.idx));
    onChange(selected.filter(x => !drop.has(x)));
  };

  if (cameras.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No cameras configured.</div>;
  }

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
        padding: 6, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-alt)',
        cursor: 'pointer', minHeight: 38 }}
        onClick={() => setOpen(o => !o)}>
        {selected.length === 0 && (
          <span style={{ fontSize: 13, color: 'var(--text-muted)', padding: '4px 6px' }}>
            Click to choose cameras…
          </span>
        )}
        {selected.map(idx => (
          <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600,
            padding: '4px 6px 4px 10px', borderRadius: 20, background: 'var(--primary)', color: '#fff' }}>
            {byIdx.get(idx)?.name || `Camera ${idx}`}
            <button type="button" title="Remove"
              onClick={e => { e.stopPropagation(); remove(idx); }}
              style={{ border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer',
                fontSize: 13, lineHeight: 1, padding: '0 2px', opacity: 0.85 }}>✕</button>
          </span>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', paddingRight: 4 }}>
          {selected.length} of {cameras.length} ▾
        </span>
      </div>

      {open && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: 'var(--shadow, 0 4px 16px rgba(0,0,0,.15))', padding: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input autoFocus style={{ ...inp, flex: '1 1 180px' }} placeholder={`Search ${cameras.length} cameras…`}
              value={q} onChange={e => setQ(e.target.value)} onClick={e => e.stopPropagation()} />
            <button type="button" style={{ ...btn, padding: '6px 10px', fontSize: 12 }} onClick={selectFiltered}>
              Select {q ? `filtered (${filtered.length})` : 'all'}
            </button>
            <button type="button" style={{ ...btn, padding: '6px 10px', fontSize: 12 }} onClick={clearFiltered}>
              Clear {q ? 'filtered' : 'all'}
            </button>
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px' }}>
            {filtered.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 8 }}>No cameras match “{q}”.</div>
              : groups.map(([zone, cams]) => (
                  <div key={zone || '_all'}>
                    {hasZones && (
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', margin: '8px 0 4px', position: 'sticky', top: 0, background: 'var(--surface)' }}>
                        {zone} ({cams.length})
                      </div>
                    )}
                    {cams.map(c => (
                      <label key={c.idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '5px 4px', cursor: 'pointer', borderRadius: 6 }}>
                        <input type="checkbox" checked={selected.includes(c.idx)} onChange={() => toggle(c.idx)} />
                        {c.name}
                      </label>
                    ))}
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AccessManagementPage() {
  const [tab, setTab]       = useState('users');
  const [menus, setMenus]   = useState([]);
  const [roles, setRoles]   = useState({});
  const [users, setUsers]   = useState([]);
  const [cameras, setCameras] = useState([]);
  const [admin, setAdmin]   = useState(false);   // server's verdict (full-access roles count)
  const [me, setMe]         = useState(null);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState(null);
  const [reveal, setReveal] = useState(null);   // { username, password } shown once
  const [toast, setToast]   = useState(null);   // { msg, type } — same pk-toast pattern used app-wide

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const loadAll = useCallback(async () => {
    setLoad(true); setErr(null);
    try {
      const ur = await apiFetch('/ui/web-users');
      if (!ur.ok) throw new Error();
      const us = await ur.json();
      setUsers(us.users || []);
      setAdmin(!!us.is_admin);
      setMe(us.me || null);
      // Roles, menus and cameras are admin-only — an end user never asks for them.
      if (us.is_admin) {
        const [mn, rl, cm] = await Promise.all([
          apiFetch('/ui/menus').then(r => r.json()),
          apiFetch('/ui/roles').then(r => r.json()),
          apiFetch('/ui/cameras').then(r => r.json()),
        ]);
        setMenus(mn.menus || []);
        setRoles(rl.roles || {});
        setCameras(cm.cameras || []);
      } else {
        setTab('users');
      }
    } catch {
      setErr('Failed to load — you may not have access to Users & Roles.');
    } finally { setLoad(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div style={{ padding: '22px 26px', overflowY: 'auto', height: '100%' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--primary)' }}>Access control</div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '4px 0 14px' }}>{admin ? <>Users &amp; Roles</> : 'Users'}</h1>

      {admin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['users', 'roles'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ ...btn, ...(tab === t ? btnPri : {}), textTransform: 'capitalize' }}>{t}</button>
          ))}
        </div>
      )}

      {loading ? <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
        : err ? <div style={{ color: 'var(--danger, #ef4444)' }}>{err}</div>
        : tab === 'users' || !admin
          ? <UsersPanel users={users} roles={roles} admin={admin} me={me} reload={loadAll} onReveal={setReveal} showToast={showToast} />
          : <RolesPanel roles={roles} menus={menus} users={users} cameras={cameras} reload={loadAll} showToast={showToast} />}

      {toast && <div className={`pk-toast ${toast.type}`}>{toast.msg}</div>}

      {reveal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={() => setReveal(null)}>
          <div style={{ ...box, width: 380, marginBottom: 0 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Temporary password</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Shown once — copy it now for <b>{reveal.username}</b>. They'll be asked to change it on first login.</p>
            <div style={{ ...inp, fontSize: 16, letterSpacing: '0.05em', userSelect: 'all', textAlign: 'center', padding: 12 }}>{reveal.password}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button style={btn} onClick={() => navigator.clipboard?.writeText(reveal.password)}>Copy</button>
              <button style={btnPri} onClick={() => setReveal(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersPanel({ users, roles, admin, me, reload, onReveal, showToast }) {
  const [nu, setNu]   = useState('');
  const [nr, setNr]   = useState('enduser');
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);   // user pending delete confirmation
  const [pwTarget, setPwTarget]   = useState(null);   // user whose password is being set
  const [resetTarget, setResetTarget] = useState(null); // user pending reset confirmation
  // The built-in support role (PRVSK-109) belongs to the support account alone.
  const roleKeys = Object.keys(roles).filter(r => !roles[r]?.system);

  const create = async () => {
    if (!nu.trim()) return;
    setBusy(true);
    try {
      const r = await apiFetch('/ui/web-users', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: nu.trim(), role: nr }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setNu(''); onReveal({ username: d.username, password: d.temp_password }); reload();
    } catch (e) { showToast?.(e.message, 'err'); } finally { setBusy(false); }
  };
  const patch = async (id, body) => {
    const r = await apiFetch(`/ui/web-users/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const d = await r.json();
    if (!r.ok) { showToast?.(d.detail || 'Failed', 'err'); return false; }
    if (d.temp_password) onReveal({ username: shownName(users.find(u => u.id === id)), password: d.temp_password });
    reload();
    return true;
  };
  const del = async (u) => {
    const r = await apiFetch(`/ui/web-users/${u.id}`, { method: 'DELETE' });
    const d = await r.json();
    if (!r.ok) { showToast?.(d.detail || 'Failed to delete user', 'err'); return; }
    reload();
    showToast?.(`User "${shownName(u)}" deleted`);
  };

  return (
    <>
      <div style={{ ...box, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13 }}>Add user</b>
        <input style={inp} placeholder="username" value={nu} onChange={e => setNu(e.target.value)} />
        <select style={inp} value={nr} onChange={e => setNr(e.target.value)}>
          {admin
            ? roleKeys.map(r => <option key={r} value={r}>{roles[r].label || r}</option>)
            : NONADMIN_ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button style={btnPri} onClick={create} disabled={busy || !nu.trim()}>+ Create</button>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>a temporary password is generated and shown once</span>
      </div>

      <div style={{ ...box, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Username</th><th style={th}>Role</th><th style={th}>Status</th><th style={th}>Actions</th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={td}><b>{shownName(u)}</b>{u.support && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>support</span>}{u.resetRequired && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--warning, #d97706)' }}>reset pending</span>}</td>
                <td style={td}>
                  {u.builtin_support
                    ? <span>{roles[u.role]?.label || 'Support (read-only)'}</span>
                    : admin
                    ? <select style={{ ...inp, padding: '5px 8px' }} value={u.role} onChange={e => patch(u.id, { role: e.target.value })}>
                        {roleKeys.map(r => <option key={r} value={r}>{roles[r].label || r}</option>)}
                      </select>
                    : <span>{BUILTIN_LABELS[u.role] || u.role}</span>}
                </td>
                <td style={td}>
                  {/* The built-in support account is switched on from the Support page,
                      which sets its expiry and makes its password (PRVSK-109). */}
                  {u.builtin_support
                    ? (u.active
                        ? <button style={{ ...btn, padding: '4px 10px', background: '#ECFDF5', color: '#047857' }}
                            onClick={() => patch(u.id, { active: false })}>On — turn off</button>
                        : <a href="/support" style={{ fontSize: 12 }}>Off — turn on from Support</a>)
                    : !admin && u.username === me
                    ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Active (you)</span>
                    : <button style={{ ...btn, padding: '4px 10px', background: u.active ? '#ECFDF5' : 'var(--surface-alt)', color: u.active ? '#047857' : 'var(--text-muted)' }}
                        onClick={() => patch(u.id, { active: !u.active })}>{u.active ? 'Active' : 'Disabled'}</button>}
                </td>
                <td style={{ ...td, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {admin && !u.builtin_support && <>
                    <button style={{ ...btn, padding: '4px 10px' }} onClick={() => setResetTarget(u)}>Reset pw</button>
                    <button style={{ ...btn, padding: '4px 10px' }} onClick={() => setPwTarget(u)}>Set pw</button>
                  </>}
                  {u.can_delete && <button style={{ ...btn, padding: '4px 10px', color: '#ef4444' }} onClick={() => setDelTarget(u)}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {admin
          ? 'Reset pw = auto-generate a temporary password (shown once). Set pw = choose one directly. Admins never need the old password.'
          : 'Click a status to enable or disable that user. A disabled user is signed out straight away. You can delete users created by a site admin.'}
      </p>

      {pwTarget && (
        <SetPasswordDialog user={pwTarget} onCancel={() => setPwTarget(null)}
          onSave={async pw => {
            if (await patch(pwTarget.id, { new_password: pw })) {
              showToast?.(`Password set for ${shownName(pwTarget)}. They'll be asked to change it at next login.`);
              setPwTarget(null);
            }
          }} />
      )}

      {resetTarget && (
        <ConfirmDialog title={`Reset password for ${shownName(resetTarget)}?`}
          message="Their current password stops working at once. A new temporary password is shown once — copy it before closing."
          confirmLabel="Reset password" confirmClass="danger"
          onConfirm={() => { const u = resetTarget; setResetTarget(null); patch(u.id, { reset_password: true }); }}
          onCancel={() => setResetTarget(null)} />
      )}

      {delTarget && (
        <ConfirmDialog title={`Delete user ${shownName(delTarget)}?`} confirmLabel="Delete" confirmClass="danger"
          onConfirm={() => { const u = delTarget; setDelTarget(null); del(u); }}
          onCancel={() => setDelTarget(null)} />
      )}
    </>
  );
}

// In-app replacement for window.prompt — same look as the temporary-password
// popup above. Enter saves, Escape or a click outside cancels.
function SetPasswordDialog({ user, onSave, onCancel }) {
  const [pw, setPw]     = useState('');
  const [busy, setBusy] = useState(false);
  const short = pw.length < 4;
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel]);
  const save = async e => {
    e.preventDefault();
    if (short || busy) return;
    setBusy(true);
    try { await onSave(pw); } finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }} onClick={onCancel}>
      <form style={{ ...box, width: 380, maxWidth: '100%', marginBottom: 0 }} onClick={e => e.stopPropagation()} onSubmit={save}>
        <h3 style={{ marginTop: 0 }}>Set password</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          New password for <b>{shownName(user)}</b>. They'll be asked to change it at their next login.
        </p>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <PasswordInput id="set-pw-input" autoFocus autoComplete="new-password" placeholder="At least 4 characters"
          style={{ ...inp, width: '100%', fontSize: 14 }} value={pw} onChange={e => setPw(e.target.value)} />
        {pw && short && <div style={{ fontSize: 12, color: 'var(--danger, #ef4444)', marginTop: 6 }}>Use at least 4 characters.</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button type="button" style={btn} onClick={onCancel}>Cancel</button>
          <button type="submit" style={{ ...btnPri, opacity: short || busy ? 0.6 : 1 }} disabled={short || busy}>
            {busy ? 'Saving…' : 'Set password'}
          </button>
        </div>
      </form>
    </div>
  );
}

function RolesPanel({ roles, menus, users, cameras, reload, showToast }) {
  const [editing, setEditing] = useState(null);   // role name being edited (or '__new__')
  const [name, setName]   = useState('');
  const [label, setLabel] = useState('');
  const [sel, setSel]     = useState([]);         // selected menu keys, or ['*']
  // Camera visibility restriction — null = unrestricted (sees every camera,
  // the default), an array = only those idx's. Mirrors the backend's
  // ROLES.<role>.allowed_cameras (see parvekshak_api.py _allowed_camera_indices).
  const [camRestricted, setCamRestricted] = useState(false);
  const [camSel, setCamSel] = useState([]);       // selected camera idx's when restricted
  const [confirmEmptyCams, setConfirmEmptyCams] = useState(false);
  const [delTarget, setDelTarget] = useState(null);   // role name pending delete confirmation
  const grouped = useMemo(() => ({
    general: menus.filter(m => !m.industrial),
    industrial: menus.filter(m => m.industrial),
  }), [menus]);
  const usersByRole = useMemo(() => {
    const m = {}; users.forEach(u => { m[u.role] = (m[u.role] || 0) + 1; }); return m;
  }, [users]);

  const startEdit = (rn) => {
    setEditing(rn);
    if (rn === '__new__') { setName(''); setLabel(''); setSel([]); setCamRestricted(false); setCamSel([]); }
    else {
      setName(rn); setLabel(roles[rn].label || rn); setSel(roles[rn].menus || []);
      const ac = roles[rn].allowed_cameras;
      setCamRestricted(Array.isArray(ac));
      setCamSel(Array.isArray(ac) ? ac : []);
    }
  };
  const allSelected = sel.includes('*');
  const toggleMenu = (k) => setSel(s => s.includes(k) ? s.filter(x => x !== k) : [...s.filter(x => x !== '*'), k]);
  const toggleAll = () => setSel(s => s.includes('*') ? [] : ['*']);

  const save = async () => {
    if (!name.trim()) { showToast('Role name required', 'err'); return; }
    if (!allSelected && camRestricted && camSel.length === 0) { setConfirmEmptyCams(true); return; }
    await doSave();
  };
  const doSave = async () => {
    const body = { name: name.trim(), label: label.trim(), menus: sel,
                   allowed_cameras: (!allSelected && camRestricted) ? camSel : null };
    const r = await apiFetch('/ui/roles', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const d = await r.json(); if (!r.ok) { showToast(d.detail || 'Failed to save role', 'err'); return; }
    setEditing(null); reload();
    // PRVSK-65: pick up a change to *this* session's own role/menus immediately
    // rather than waiting for the poll/focus refetch in App.jsx's Guard — other
    // open sessions for the edited role still pick it up via that refetch.
    refreshEntitlements(apiFetch);
    showToast(`Role "${d?.roles?.[name.trim()]?.label || name.trim()}" saved`);
  };
  const del = async (rn) => {
    const r = await apiFetch(`/ui/roles/${encodeURIComponent(rn)}`, { method: 'DELETE' });
    const d = await r.json(); if (!r.ok) { showToast(d.detail || 'Failed to delete role', 'err'); return; }
    reload();
    showToast(`Role "${rn}" deleted`);
  };

  const BUILTIN = ['admin', 'enduser'];

  return (
    <>
      <div style={{ ...box, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <b style={{ fontSize: 13 }}>Roles</b>
        <button style={btnPri} onClick={() => startEdit('__new__')}>+ New role</button>
      </div>

      <div style={{ ...box, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Role</th><th style={th}>Menus</th><th style={th}>Cameras</th><th style={th}>Users</th><th style={th}>Actions</th></tr></thead>
          <tbody>
            {Object.keys(roles).filter(rn => !roles[rn]?.system).map(rn => (
              <tr key={rn}>
                <td style={td}><b>{roles[rn].label || rn}</b> <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({rn})</span></td>
                <td style={td}>{(roles[rn].menus || []).includes('*') ? 'All menus' : `${(roles[rn].menus || []).length} menus`}</td>
                <td style={td}>
                  {(roles[rn].menus || []).includes('*') || !Array.isArray(roles[rn].allowed_cameras)
                    ? 'All cameras' : `${roles[rn].allowed_cameras.length} camera${roles[rn].allowed_cameras.length === 1 ? '' : 's'}`}
                </td>
                <td style={td}>{usersByRole[rn] || 0}</td>
                <td style={{ ...td, display: 'flex', gap: 6 }}>
                  <button style={{ ...btn, padding: '4px 10px' }} onClick={() => startEdit(rn)}>Edit menus</button>
                  {!BUILTIN.includes(rn) && <button style={{ ...btn, padding: '4px 10px', color: '#ef4444' }} onClick={() => setDelTarget(rn)}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={box}>
          <h3 style={{ marginTop: 0 }}>{editing === '__new__' ? 'New role' : `Edit “${editing}”`}</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <input style={inp} placeholder="role id (e.g. supervisor)" value={name} disabled={editing !== '__new__'} onChange={e => setName(e.target.value.replace(/\s+/g, '_').toLowerCase())} />
            <input style={inp} placeholder="Display label" value={label} onChange={e => setLabel(e.target.value)} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontWeight: 600 }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Full access (all menus)
          </label>
          {!allSelected && ['general', 'industrial'].map(g => (
            <div key={g} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>{g}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {grouped[g].map(m => (
                  <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 20, cursor: 'pointer', background: sel.includes(m.key) ? 'var(--primary)' : 'var(--surface)', color: sel.includes(m.key) ? '#fff' : 'var(--text)' }}>
                    <input type="checkbox" style={{ display: 'none' }} checked={sel.includes(m.key)} onChange={() => toggleMenu(m.key)} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {!allSelected && (
            <div style={{ marginTop: 4, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontWeight: 600 }}>
                <input type="checkbox" checked={camRestricted} onChange={e => setCamRestricted(e.target.checked)} />
                Limit this role to specific cameras
              </label>
              {!camRestricted && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                  Unchecked — this role sees every camera.
                </div>
              )}
              {camRestricted && (
                <>
                  <div style={{ marginTop: 6 }}>
                    <CameraPicker cameras={cameras} selected={camSel} onChange={setCamSel} />
                  </div>
                  {camSel.length === 0 && (
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
                      ⚠ No cameras picked yet — saving now will hide every camera from this role.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={btnPri} onClick={save}>Save role</button>
            <button style={btn} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {confirmEmptyCams && (
        <ConfirmDialog title="Save with no cameras picked?"
          message="This role will see NO cameras at all until you add some."
          confirmLabel="Save anyway" confirmClass="danger"
          onConfirm={() => { setConfirmEmptyCams(false); doSave(); }}
          onCancel={() => setConfirmEmptyCams(false)} />
      )}
      {delTarget && (
        <ConfirmDialog title={`Delete role "${delTarget}"?`} confirmLabel="Delete" confirmClass="danger"
          onConfirm={() => { const rn = delTarget; setDelTarget(null); del(rn); }}
          onCancel={() => setDelTarget(null)} />
      )}
    </>
  );
}
